import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { getAllTiers } from '../../config/subscriptionTiers'
import routes from '../../pages/routes'
import { sessionState } from '../../state'
import { OfficeSelector } from '../subscription'

import PricingCard from './PricingCard'

interface PricingModalProps {
  open: boolean
  onClose: () => void
}

export default function PricingModal({ open, onClose }: PricingModalProps) {
  const navigate = useNavigate()
  const session = useRecoilValue(sessionState)
  const tiers = getAllTiers()
  const [selectedTier, setSelectedTier] = useState<string | null>(null)
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>(
    'annual'
  )
  const [officeSelectorOpen, setOfficeSelectorOpen] = useState(false)

  const handleSelectTier = (tierId: string, interval: 'monthly' | 'annual') => {
    if (tierId === 'free') {
      // Free tier - close modal and navigate to offices page
      onClose()
      navigate(routes.offices)
    } else {
      // Paid tier - show office selector
      setSelectedTier(tierId)
      setBillingInterval(interval)
      setOfficeSelectorOpen(true)
    }
  }

  const handleCloseOfficeSelector = () => {
    setOfficeSelectorOpen(false)
    setSelectedTier(null)
    // Close the pricing modal as well after office selection
    onClose()
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            borderRadius: 3
          }
        }}
      >
        <Box sx={{ position: 'relative', p: 4 }}>
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 800,
                color: '#ffffff',
                mb: 2,
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
              }}
            >
              Choose Your Plan
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 400
              }}
            >
              Scale your office capacity as your team grows
            </Typography>
          </Box>

          {/* Pricing Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)'
              },
              gap: 3,
              mb: 3
            }}
          >
            {tiers.map(tier => {
              const isRecommended = tier.id === 'medium'

              return (
                <PricingCard
                  key={tier.id}
                  tier={tier}
                  isRecommended={isRecommended}
                  isSignedIn={true}
                  billingInterval={billingInterval}
                  onBillingIntervalChange={setBillingInterval}
                  onSelect={handleSelectTier}
                />
              )
            })}
          </Box>

          {/* Footer Note */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant='body2'
              sx={{
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              All plans include unlimited desks and full booking features
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* Office Selector Modal */}
      {session.user && selectedTier && (
        <OfficeSelector
          open={officeSelectorOpen}
          tierId={selectedTier}
          billingInterval={billingInterval}
          userId={session.user.uid || ''}
          onClose={handleCloseOfficeSelector}
        />
      )}
    </>
  )
}
